// Atomic UI primitives — match the design tokens in styles/tokens.css.
import type { CSSProperties } from 'react';
import type { MatchTeamStatus, TeamId } from '../types';
import { STATUS, teamById } from '../mock/data';
import { publicMatchCode } from '../lib/match-code';

export function ProtoLightningMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 1042 1042" fill="none" aria-hidden>
      <path d="M331.831,497.671c6.856,-0 13.711,-2.409 18.9,-6.671l397.279,-322.603c7.968,-6.485 18.901,2.965 13.712,11.674l-173.253,299.995c-4.447,7.783 1.112,17.418 10.191,17.418l282.207,0c5.003,0 9.265,-4.076 9.265,-9.265l0,-110.437c0,-5.003 -4.076,-9.265 -9.265,-9.265l-63.186,0c-7.042,0 -11.489,-7.597 -7.968,-13.712l192.524,-332.978c3.521,-6.115 -0.926,-13.713 -7.968,-13.713l-240.886,0c-7.783,0 -15.565,2.78 -21.68,7.598l-577.387,468.802c-5.373,4.447 -2.223,13.156 4.633,13.156l172.883,0Z" fill="var(--logo-fill)" />
      <path d="M709.651,544.18c-6.856,0 -13.712,2.409 -18.901,6.671l-397.279,322.603c-7.967,6.485 -18.9,-2.965 -13.712,-11.674l173.253,-299.995c4.447,-7.783 -1.111,-17.418 -10.191,-17.418l-282.207,-0c-5.003,-0 -9.265,4.076 -9.265,9.265l0,110.437c0,5.003 4.077,9.265 9.265,9.265l63.186,-0c7.042,-0 11.489,7.597 7.968,13.712l-192.338,332.794c-3.521,6.115 0.926,13.712 7.967,13.712l240.887,0c4.261,0 8.522,-0.833 12.509,-2.378c3.296,-1.276 6.404,-3.039 9.171,-5.219l577.387,-468.618c5.373,-4.447 2.223,-13.156 -4.633,-13.156l-173.068,-0Z" fill="var(--logo-fill)" />
    </svg>
  );
}

export function ProtoStatusPill({
  status,
  size = 'sm',
  inspection,
}: {
  status: MatchTeamStatus;
  size?: 'sm' | 'lg';
  /**
   * For at_inspection / l3 / l2 statuses, the count of robots that have
   * completed all 3 stations. Renders as "At Inspection · 1/3" so the
   * pill carries per-team progress without needing extra chips. Showing
   * the count on L3/L2 is the operator's accountability check — if a
   * team advanced to L3 with only 2 of 3 robots ready (override), the
   * pill makes that visible at the match-card level so missing robots
   * can't quietly slip through post-inspection.
   */
  inspection?: { passed: number; total: number };
}) {
  // Treat legacy per-station statuses (s1/s2/s3) as at_inspection — those
  // values are no longer written by the per-robot model but may exist in
  // older DB rows.
  const normalizedStatus: MatchTeamStatus =
    status === 's1' || status === 's2' || status === 's3' ? 'at_inspection' : status;
  const s = STATUS[normalizedStatus];
  if (!s) return null;
  const live = normalizedStatus === 'in_game';
  const cls = live ? 'pill live' : `pill ${s.tone}`;
  // Larger pill variant adds padding + font-size; the base .pill class in
  // tokens.css carries the small defaults.
  const sizeCls = size === 'lg' ? ' pad-pill-lg txt-12' : '';
  const COUNTABLE: MatchTeamStatus[] = ['at_inspection', 'l3', 'l2'];
  const showCount = COUNTABLE.includes(normalizedStatus) && inspection && inspection.total > 0;
  return (
    <span className={cls + sizeCls}>
      {!live && <span className="dot" />}
      {live && <span style={{ width: 6, height: 6, borderRadius: 999, background: '#0A0A0F' }} />}
      {s.label}
      {showCount && (
        <span className="fw-semi" style={{ marginLeft: 5, opacity: 0.85 }}>
          · {inspection!.passed}/{inspection!.total}
        </span>
      )}
    </span>
  );
}

// Statuses staff/ops can set manually from a status pill. Deliberately stops
// at l2 (cleared, ready to play). in_game + completed are NOT here: setting
// them straight from the pill bypasses the advance / mark-completed flow that
// records rounds and moves the live/on-deck pointers. A raw pill-set to
// 'completed' produced a result-less "completed" match that dropped out of the
// schedule (both sides completed) AND never appeared in results (no rounds).
// Those transitions belong to ADVANCE_MATCH and the Mark Completed modal.
// Forfeit (V/IV), rescheduled, and direct_transfer are likewise excluded —
// dedicated actions (FORFEIT_MATCH / RESCHEDULE_MATCH) trigger their side
// effects. s1/s2/s3 + failed are excluded too — the per-robot model surfaces
// individual station progress + failure on the robot's queue row.
export const EDITABLE_STATUSES: readonly MatchTeamStatus[] = [
  'not_tracked', 'at_inspection',
  'l3', 'l2',
];

// Volunteers can flip a team between "not tracked" and "at inspection"
// from the schedule view — pre-queuing teams for the inspection desk
// without granting access to in_game / completed transitions (those
// belong to staff+). Server-side RLS already allows volunteer writes
// to match_state; this constant just narrows the UI affordance.
export const VOL_EDITABLE_STATUSES: readonly MatchTeamStatus[] = [
  'not_tracked', 'at_inspection',
];

export function ProtoEditableStatusPill({
  status,
  onChange,
  size = 'sm',
  inspection,
  editableStatuses = EDITABLE_STATUSES,
}: {
  status: MatchTeamStatus;
  onChange?: (next: MatchTeamStatus) => void;
  size?: 'sm' | 'lg';
  inspection?: { passed: number; total: number };
  /** Override the option list in the dropdown. Defaults to the staff/ops
   *  full set; pass VOL_EDITABLE_STATUSES (or a custom list) to restrict. */
  editableStatuses?: readonly MatchTeamStatus[];
}) {
  const normalizedStatus: MatchTeamStatus =
    status === 's1' || status === 's2' || status === 's3' ? 'at_inspection' : status;
  // Read-only when there's no handler (public role / display contexts) OR the
  // team is mid-match (in_game ⇒ this is the live match). The live match's
  // status pills are locked for everyone, ops included — changing a pill
  // mid-match is never the intent.
  if (!onChange || normalizedStatus === 'in_game') {
    return <ProtoStatusPill status={status} size={size} inspection={inspection} />;
  }
  // in_game returned read-only above, so an editable pill is never the live tone.
  const meta = STATUS[normalizedStatus];
  if (!meta) return null;
  const cls = `pill ${meta.tone}`;
  const sizeCls = size === 'lg' ? ' pad-pill-lg txt-12' : '';
  // Same triple of countable statuses as ProtoStatusPill — at_inspection
  // for in-progress, l3/l2 for ready/clearance display so post-inspection
  // anomalies (e.g., advanced with 2 of 3 robots via override) stay visible.
  const COUNTABLE: MatchTeamStatus[] = ['at_inspection', 'l3', 'l2'];
  const showCount = COUNTABLE.includes(normalizedStatus) && inspection && inspection.total > 0;
  // A completed/rescheduled match may still render this pill in some contexts.
  // Those statuses aren't in EDITABLE_STATUSES, so include the current status
  // here to keep the controlled <select> valid — it shows as the current value
  // but is never an offered transition target for any OTHER match.
  let options = editableStatuses.includes(normalizedStatus)
    ? [...editableStatuses]
    : [...editableStatuses, normalizedStatus];
  // Restricted editors (volunteers/staff — their list omits the l3/l2 clearance
  // statuses) get exactly one affordance: queue an untracked team for the
  // inspection desk (not_tracked → at_inspection). They can't set a team back
  // to Untracked, and can't bump a progressed team back to inspection — so the
  // only target ever offered is at_inspection, and only from not_tracked. Ops
  // keep full flexibility. The current value is always kept so the <select>
  // stays valid.
  const isRestrictedEditor = !editableStatuses.includes('l3');
  if (isRestrictedEditor) {
    options = options.filter((s) =>
      s === normalizedStatus ||
      (s === 'at_inspection' && normalizedStatus === 'not_tracked'));
  }
  // Nothing left to change (only the current value remains) → render read-only.
  if (options.length === 1 && options[0] === normalizedStatus) {
    return <ProtoStatusPill status={status} size={size} inspection={inspection} />;
  }

  return (
    <div className={cls + sizeCls + ' relative inline-flex gap-2 cursor-pointer'}>
      <span className="dot" />
      <span className="inline-flex">
        {meta.label}
        {showCount && (
          <span className="fw-semi" style={{ marginLeft: 5, opacity: 0.85 }}>
            · {inspection!.passed}/{inspection!.total}
          </span>
        )}
        <span className="ml-1 txt-9" style={{ opacity: 0.6 }}>▾</span>
      </span>
      <select
        value={normalizedStatus}
        onChange={(e) => onChange(e.target.value as MatchTeamStatus)}
        title="Click to change status"
        className="cursor-pointer border-none m-0 p-0"
        style={{
          position: 'absolute', inset: 0, opacity: 0,
          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
          font: 'inherit', color: 'inherit', background: 'transparent',
        }}
      >
        {options.map((s) => (
          <option key={s} value={s} style={{ color: 'var(--text-0)', background: 'var(--bg-1)' }}>
            {STATUS[s].label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function ProtoTeamChip({
  id,
  side,
  big,
  full,
  logo,
  size,
  onClick,
}: {
  id: TeamId;
  side?: 'red' | 'blue';
  big?: boolean;
  /** Render the team's full registered name instead of its short
   *  display name. Used by the Teams directory list where the column
   *  is wide enough to spell things out (and the short name is already
   *  visible in the adjacent id column). */
  full?: boolean;
  /** Show the team's logo inline after the name (schedule pages, to help
   *  spectators visually identify teams). Hotlinked from /team-logos/<id>.png
   *  (served same-origin); a missing file just hides the <img>. */
  logo?: boolean;
  /** Explicit name font-size in px; also scales the side swatch + logo to match.
   *  Used by the schedules / Teams list to give the bigger, easier-to-read chips. */
  size?: number;
  onClick?: () => void;
}) {
  const t = teamById(id);
  // When onClick is supplied the chip becomes a navigable link; the
  // .team-chip-link modifier carries the cursor + yellow-on-hover style
  // (rule lives in tokens.css alongside the base .team-chip).
  return (
    <span
      className={`team-chip ${onClick ? 'team-chip-link' : ''}`}
      onClick={onClick}
      title={onClick ? `Open ${t.name}` : undefined}
    >
      {side && <span className={`team-swatch ${side}`} style={size ? { height: size } : undefined} />}
      {logo && <TeamLogo id={id} size={size ?? 18} />}
      <span className={`team-name${big ? ' txt-22' : ''}`} style={size ? { fontSize: size } : undefined}>{full ? t.name : t.short}</span>
    </span>
  );
}

// Small inline team logo for the schedule / teams pages. Decorative (the name
// carries the meaning), so aria-hidden. Team logos come both dark- and light-on-
// transparent, so the <img> sits on a fixed neutral mid-grey tile (regardless of
// theme) — enough contrast for either end of the spectrum.
// A missing/failed asset removes the whole tile, not just the image.
export function TeamLogo({ id, size = 18 }: { id: TeamId; size?: number }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxSizing: 'border-box',
        height: size, padding: '2px 3px',
        background: '#8A8A93', borderRadius: 4,
      }}
    >
      <img
        src={`/team-logos/${id}.png`}
        alt=""
        aria-hidden="true"
        loading="lazy"
        onError={(e) => {
          const tile = (e.currentTarget as HTMLImageElement).parentElement;
          if (tile) tile.style.display = 'none';
        }}
        style={{ maxHeight: '100%', maxWidth: size + 12, objectFit: 'contain', display: 'block' }}
      />
    </span>
  );
}

export function ProtoMatchCode({
  code,
  live,
  onDeck,
}: {
  code: string;
  live?: boolean;
  onDeck?: boolean;
}) {
  // Live matches use the accent palette; on-deck darkens the background
  // and bumps the border to line-strong. Both states are dynamic per
  // match, so the color/border swatch stays inline; the layout + type
  // sizing moves to utility classes.
  const style: CSSProperties = {
    background: live ? 'var(--accent)' : onDeck ? 'var(--bg-3)' : 'var(--bg-2)',
    color: live ? 'var(--accent-ink)' : 'var(--text-0)',
    border: 'var(--bw) solid ' + (live ? 'var(--accent)' : onDeck ? 'var(--line-strong)' : 'var(--line)'),
  };
  return (
    <span className="mono inline-flex txt-12 fw-semi r-sm tracking-2" style={{ ...style, padding: '3px 7px' }}>
      {publicMatchCode(code)}
    </span>
  );
}

// Inspector-progress dot-track shown in the inspection panel header. Takes
// the inspector's currentStation (s1/s2/s3/l3) — not the team's match status.
// Renders 6 pips representing the stages a robot moves through.
export function ProtoStationTrack({ status }: { status: MatchTeamStatus }) {
  const stages: MatchTeamStatus[] = ['at_inspection', 's1', 's2', 's3', 'l3', 'in_game'];
  const idx = stages.indexOf(status);
  return (
    <div className="station-track" style={{ gap: 3 }}>
      {stages.map((st, i) => {
        let cls = 'station-pip';
        if (status === 'completed') cls += ' done';
        else if (idx === -1) {
          /* untracked — leave blank */
        } else if (i < idx) cls += ' done';
        else if (i === idx) cls += ' active';
        return <span key={st} className={cls} style={{ width: 16, height: 6 }} />;
      })}
    </div>
  );
}
